import React, { useState, useCallback, useRef } from 'react';
import { X, Sparkles, ExternalLink, TrendingUp, AlertTriangle, Tag, Search, Loader2 } from 'lucide-react';
import { PhotoAiLens } from '../types';
import { roundSEK } from '../utils/lensAggregation';

interface AiLensOverlayProps {
  aiLens: PhotoAiLens;
  onClose: () => void;
  onReanalyze?: () => void;
}

const DISMISS_THRESHOLD = 0.3;
const VELOCITY_THRESHOLD = 0.5; // px/ms

/**
 * Bottom-sheet overlay showing AI Lens analysis results.
 * Slides up on open and slides down on close. Swipe down to dismiss.
 */
const AiLensOverlay: React.FC<AiLensOverlayProps> = ({ aiLens, onClose, onReanalyze }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [dragY, setDragY] = useState(0);
  const sheetRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const startTime = useRef(0);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      onClose();
    }
  }, [isClosing, onClose]);

  // --- Touch handlers for swipe-to-dismiss ---
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    isDragging.current = true;
    startY.current = e.touches[0].clientY;
    currentY.current = 0;
    startTime.current = Date.now();
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const delta = e.touches[0].clientY - startY.current;
    const clamped = Math.max(0, delta);
    currentY.current = clamped;
    setDragY(clamped);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const sheetHeight = sheetRef.current?.offsetHeight || 300;
    const elapsed = Date.now() - startTime.current;
    const velocity = currentY.current / Math.max(elapsed, 1);

    if (currentY.current > sheetHeight * DISMISS_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      handleClose();
    } else {
      setDragY(0);
    }
  }, [handleClose]);

  if (aiLens.status !== 'completed' || !aiLens.result) return null;

  const { result, market } = aiLens;

  // Determine value display
  const aiValue = result.estimatedValue;
  const marketValue = market?.medianPrice;
  const displayValue = marketValue || aiValue;
  const belowThreshold = displayValue !== undefined && displayValue < 300;

  // Confidence label
  const getConfidenceLabel = (confidence: number): { label: string; color: string } => {
    if (confidence >= 0.8) return { label: 'Hög', color: 'text-emerald-600 dark:text-emerald-400' };
    if (confidence >= 0.5) return { label: 'Medium', color: 'text-amber-600 dark:text-amber-400' };
    return { label: 'Låg', color: 'text-red-500 dark:text-red-400' };
  };

  const confidence = getConfidenceLabel(result.confidence);

  return (
    <div
      className={`absolute inset-0 flex items-end lg:items-center justify-center transition-colors duration-300 ${isClosing ? 'bg-transparent' : 'bg-black/20'}`}
      style={{
        zIndex: 65,
        ...(dragY > 0 && !isClosing ? { backgroundColor: `rgba(0,0,0,${0.2 * Math.max(0, 1 - dragY / (sheetRef.current?.offsetHeight || 300))})` } : {}),
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={sheetRef}
        className={`bg-[var(--bg-surface)] rounded-t-2xl lg:rounded-2xl w-full lg:max-w-lg lg:mb-8 max-h-[70vh] overflow-y-auto shadow-2xl ${dragY > 0 ? '' : isClosing ? 'animate-sheet-down' : 'animate-sheet-up'}`}
        style={{
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          ...(dragY > 0 ? { transform: `translateY(${dragY}px)`, transition: 'none' } : {}),
        }}
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={handleAnimationEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-[var(--text-tertiary)] rounded-full opacity-30" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-4 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center">
              <Sparkles size={16} className="text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[var(--text-primary)] leading-tight">
                {result.objectName}
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs font-medium px-1.5 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded">
                  {result.category}
                </span>
                <span className={`text-xs font-medium ${confidence.color}`}>
                  {confidence.label} säkerhet
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="-m-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
            aria-label="Stäng"
          >
            <X size={18} />
          </button>
        </div>

        {/* Below threshold warning */}
        {belowThreshold && (
          <div className="mx-4 mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
              Under auktionstruöskel (300 kr)
            </p>
          </div>
        )}

        {/* Value section */}
        <div className="px-4 pb-3">
          <div className="grid grid-cols-2 gap-3">
            {/* AI estimate */}
            {aiValue !== undefined && (
              <div className="bg-[var(--bg-surface-alt)] rounded-xl p-3">
                <p className="text-[12px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                  AI-uppskattning
                </p>
                <p className="text-lg font-bold text-[var(--text-primary)]">
                  {roundSEK(aiValue).toLocaleString('sv-SE')} kr
                </p>
              </div>
            )}

            {/* Market value */}
            {market && market.medianPrice !== undefined && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3">
                <p className="text-[12px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1">
                  Marknadsvärde
                </p>
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                  {roundSEK(market.medianPrice).toLocaleString('sv-SE')} kr
                </p>
                {market.priceRange && (
                  <p className="text-[12px] text-emerald-600/70 dark:text-emerald-400/70 mt-0.5">
                    {roundSEK(market.priceRange.min).toLocaleString('sv-SE')}–{roundSEK(market.priceRange.max).toLocaleString('sv-SE')} kr
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Market confidence & count */}
          {market && market.resultCount > 0 && (
            <div className="mt-2 space-y-1.5">
              {/* Confidence bar */}
              {market.confidence !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] whitespace-nowrap">
                    Datasäkerhet
                  </span>
                  <div className="flex-1 h-1.5 bg-[var(--bg-surface-alt)] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        market.confidence >= 0.7
                          ? 'bg-emerald-500'
                          : market.confidence >= 0.4
                            ? 'bg-amber-500'
                            : 'bg-red-400'
                      }`}
                      style={{ width: `${Math.round(market.confidence * 100)}%` }}
                    />
                  </div>
                  <span className={`text-[12px] font-bold tabular-nums ${
                    market.confidence >= 0.7
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : market.confidence >= 0.4
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-red-500 dark:text-red-400'
                  }`}>
                    {Math.round(market.confidence * 100)}%
                  </span>
                </div>
              )}
              {/* Count text */}
              <p className="text-xs text-[var(--text-tertiary)] flex items-center gap-1">
                <TrendingUp size={12} />
                {market.analyzedSales && market.totalMatches
                  ? `Baserat på ${market.analyzedSales} analyserade av ${market.totalMatches.toLocaleString('sv-SE')} föremål`
                  : `Baserat på ${market.resultCount} sålda föremål på Auctionet`
                }
              </p>
            </div>
          )}
        </div>

        {/* Description */}
        {result.description && (
          <div className="px-4 pb-3">
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {result.description}
            </p>
          </div>
        )}

        {/* Details grid */}
        <div className="px-4 pb-3">
          <div className="space-y-1.5">
            {result.brand && (
              <DetailRow label="Märke" value={result.brand} />
            )}
            {result.artist && (
              <DetailRow label="Konstnär/Formgivare" value={result.artist} />
            )}
            {result.model && (
              <DetailRow label="Modell" value={result.model} />
            )}
            {result.period && (
              <DetailRow label="Period" value={result.period} />
            )}
            {result.materials && result.materials.length > 0 && (
              <DetailRow label="Material" value={result.materials.join(', ')} />
            )}
            {result.condition && (
              <DetailRow label="Skick" value={result.condition} />
            )}
          </div>
        </div>

        {/* Search terms */}
        {result.searchTerms && result.searchTerms.length > 0 && (
          <div className="px-4 pb-3">
            <div className="flex items-center gap-1 mb-1.5">
              <Tag size={12} className="text-[var(--text-tertiary)]" />
              <span className="text-[12px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Söktermer
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {result.searchTerms.map((term, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-0.5 bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] rounded-full"
                >
                  {term}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Auctionet link */}
        {market?.searchUrl && (
          <div className="px-4 pb-3">
            <a
              href={market.searchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface-hover)] text-[var(--text-secondary)] rounded-xl text-sm font-medium transition-colors"
            >
              <Search size={14} />
              Se på Auctionet
              <ExternalLink size={12} />
            </a>
          </div>
        )}

        {/* Re-analyze button */}
        {onReanalyze && (
          <div className="px-4 pb-4">
            <button
              onClick={onReanalyze}
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-violet-100 dark:bg-violet-900/30 hover:bg-violet-200 dark:hover:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded-xl text-sm font-medium transition-colors"
            >
              <Sparkles size={14} />
              Analysera igen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-baseline text-sm">
    <span className="text-[var(--text-tertiary)] text-xs">{label}</span>
    <span className="text-[var(--text-primary)] font-medium text-right max-w-[60%]">{value}</span>
  </div>
);

export default AiLensOverlay;
